import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Sparkles } from "lucide-react";

import { cn } from "../../lib/utils";
import PromptPickerList from "./PromptPickerList";
import { isPromptUsable, TRANSCRIPT_SETUP_HINT } from "../constants/chatConstants";
import { rankPromptsForQuery } from "../utils/promptIntentUtils";
import type { CrmRecordType, ManagingPromptCatalogEntry } from "../types/chat.types";

export interface SuggestedPromptsPanelProps {
  prompts: ManagingPromptCatalogEntry[];
  query: string;
  recordType?: CrmRecordType | null;
  hasCallRecordingWithTranscript?: boolean;
  onSelect: (prompt: ManagingPromptCatalogEntry) => void;
  /** Send the current query as a free-form question (search / ask anything). */
  onAskDirectly?: () => void;
  askHighlighted?: boolean;
  promptHighlightedIndex?: number;
  highlightedIndex?: number;
  onHighlightChange?: (index: number) => void;
  className?: string;
}

function isSuggestionSelectable(
  prompt: ManagingPromptCatalogEntry,
  recordType?: CrmRecordType | null,
  hasCallRecordingWithTranscript?: boolean
): boolean {
  const { usable, setupReason } = isPromptUsable(prompt, recordType, {
    hasCallRecordingWithTranscript,
  });
  return usable || Boolean(setupReason);
}

/** Only prompts the user can act on (no hard-disabled calendar/web gates in the strip). */
export function filterSelectableSuggestions(
  ranked: ReturnType<typeof rankPromptsForQuery>,
  recordType?: CrmRecordType | null,
  hasCallRecordingWithTranscript?: boolean
): ManagingPromptCatalogEntry[] {
  return ranked
    .filter(({ prompt }) =>
      isSuggestionSelectable(prompt, recordType, hasCallRecordingWithTranscript)
    )
    .map((r) => r.prompt);
}

const SuggestedPromptsPanel: React.FC<SuggestedPromptsPanelProps> = ({
  prompts,
  query,
  recordType = null,
  hasCallRecordingWithTranscript,
  onSelect,
  onAskDirectly,
  askHighlighted = false,
  promptHighlightedIndex = -1,
  highlightedIndex = -1,
  onHighlightChange,
  className,
}) => {
  const { t } = useTranslation();
  const trimmed = query.trim();

  const selectablePrompts = useMemo(() => {
    const ranked = rankPromptsForQuery(prompts, query, {
      recordType,
      hasTranscript: hasCallRecordingWithTranscript,
    });
    return filterSelectableSuggestions(ranked, recordType, hasCallRecordingWithTranscript);
  }, [hasCallRecordingWithTranscript, prompts, query, recordType]);

  const handleSelect = (prompt: ManagingPromptCatalogEntry) => {
    onSelect(prompt);
  };

  const preventBlur = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const isChipMode = !trimmed;
  const showAskRow = Boolean(trimmed && onAskDirectly);

  if (!showAskRow && selectablePrompts.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "absolute bottom-full left-0 right-0 z-30 mb-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg",
        className
      )}
      role="presentation"
      onMouseDown={preventBlur}
    >
      {isChipMode ? (
        <div className="px-3 py-2.5">
          <p className="mb-2 text-[11px] font-medium text-gray-500">{t("Try a prompt")}</p>
          <div
            className="flex flex-wrap gap-1.5"
            role="listbox"
            aria-label={t("Suggested prompts") as string}
          >
            {selectablePrompts.map((prompt, index) => {
              const { setupReason } = isPromptUsable(prompt, recordType, {
                hasCallRecordingWithTranscript,
              });
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={prompt.promptType}
                  type="button"
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseEnter={() => onHighlightChange?.(index)}
                  onClick={() => handleSelect(prompt)}
                  title={setupReason ?? prompt.description}
                  className={cn(
                    "inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-xs font-medium transition-colors",
                    isHighlighted
                      ? "border-blue-400 bg-blue-50 text-blue-800"
                      : "border-gray-200 bg-gray-50 text-gray-800 hover:border-blue-300 hover:bg-blue-50"
                  )}
                >
                  <Sparkles className="h-3 w-3 flex-shrink-0 text-blue-600" />
                  <span className="truncate">{prompt.name}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-gray-400">
            {t("Type a question and press Enter to search CRM")}
          </p>
          {selectablePrompts.some(
            (p) =>
              isPromptUsable(p, recordType, { hasCallRecordingWithTranscript }).setupReason
          ) ? (
            <p className="mt-1 text-[10px] text-gray-400">{TRANSCRIPT_SETUP_HINT}</p>
          ) : null}
        </div>
      ) : (
        <div className="py-1" role="listbox" aria-label={t("Search and prompts") as string}>
          {showAskRow ? (
            <button
              type="button"
              role="option"
              aria-selected={askHighlighted}
              onMouseEnter={() => onHighlightChange?.(0)}
              onClick={() => onAskDirectly?.()}
              className={cn(
                "mx-1 flex w-[calc(100%-0.5rem)] items-start gap-2 rounded-lg px-3 py-2.5 text-left transition-colors",
                askHighlighted ? "bg-blue-50" : "hover:bg-gray-50"
              )}
            >
              <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-700">
                <MessageSquare className="h-3 w-3" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  {t("Ask this question")}
                </span>
                <span className="block truncate text-xs text-gray-500">{trimmed}</span>
                <span className="mt-0.5 block text-[10px] text-gray-400">
                  {t("Enter to search CRM and get an answer")}
                </span>
              </span>
            </button>
          ) : null}
          {selectablePrompts.length > 0 ? (
            <>
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {t("Or pick a prompt")}
              </p>
              <PromptPickerList
                listId="intent-suggestion-list"
                prompts={selectablePrompts}
                recordType={recordType}
                query={trimmed}
                onSelect={handleSelect}
                hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
                highlightedIndex={promptHighlightedIndex}
                onHighlightChange={(index) => onHighlightChange?.(index + 1)}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SuggestedPromptsPanel;
